/**
 * @module CanvasTimelineDock
 * @purpose Collapsible timeline dock showing 4-bucket time distribution bars.
 * @depends FacetEntry (types/analysis-artefacts), react-i18next
 * @dependedOnBy CanvasView
 *
 * @rules
 * - Performance: pure display from props
 * - State: none — expand/collapse owned by parent
 * - Errors: renders empty bars when timelineBuckets is empty
 */

import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronUp } from 'lucide-react'
import type { FacetEntry } from '../../../types/analysis-artefacts'

const DOCK_COLLAPSED_HEIGHT = '40px'
const DOCK_EXPANDED_HEIGHT = '120px'
const BAR_MAX_HEIGHT = 64
const MIN_BAR_HEIGHT = 2

interface DockBarProps {
  bucket: FacetEntry
  maxCount: number
  onClick: (id: string) => void
  isSelected: boolean
}

function DockBar({ bucket, maxCount, onClick, isSelected }: DockBarProps): React.ReactElement {
  const safeMax = maxCount > 0 ? maxCount : 1
  const barHeight =
    bucket.count > 0 ? Math.max(MIN_BAR_HEIGHT, (bucket.count / safeMax) * BAR_MAX_HEIGHT) : MIN_BAR_HEIGHT
  const barHeightPx = `${String(barHeight)}px`
  const label = `${bucket.label}: ${String(bucket.count)}`

  return (
    <button
      className="flex flex-1 flex-col items-center gap-0.5"
      title={label}
      aria-label={label}
      aria-pressed={isSelected}
      onClick={() => { onClick(bucket.id) }}
      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
    >
      <div
        className="w-full rounded-t transition-colors hover:opacity-80"
        style={{
          height: barHeightPx,
          background: isSelected
            ? 'var(--accent)'
            : bucket.count > 0
              ? 'color-mix(in srgb, var(--accent) 60%, transparent)'
              : 'var(--border)',
          minWidth: '4px',
        }}
      />
    </button>
  )
}

interface CanvasTimelineDockProps {
  isExpanded: boolean
  onToggle: () => void
  timelineBuckets: FacetEntry[]
  selectedBuckets?: string[]
  onSelectionChange?: (id: string) => void
}

/**
 * Timeline dock with 4-bucket histogram.
 *
 * @behaviour Collapsed by default; expands to show bucket bars.
 *            Clicking a bar toggles it in the filter selection via onSelectionChange.
 * @errors Renders empty bars when timelineBuckets is empty.
 */
export function CanvasTimelineDock({
  isExpanded,
  onToggle,
  timelineBuckets,
  selectedBuckets = [],
  onSelectionChange,
}: CanvasTimelineDockProps): React.ReactElement {
  const { t } = useTranslation('analysis')
  const maxCount = Math.max(...timelineBuckets.map((bucket) => bucket.count), 1)
  const dockHeight = isExpanded ? DOCK_EXPANDED_HEIGHT : DOCK_COLLAPSED_HEIGHT

  const handleBucketClick = (id: string): void => {
    onSelectionChange?.(id)
  }

  return (
    <div
      className="flex flex-col border-t transition-all"
      style={{
        borderColor: 'var(--border)',
        height: dockHeight,
        background: 'var(--bg-card)',
        overflow: 'hidden',
      }}
    >
      <div className="flex items-center justify-between px-3 py-1">
        <button
          onClick={onToggle}
          className="inline-flex items-center gap-1 text-xs font-[var(--font-mono)] tracking-wide uppercase transition-colors hover:text-[var(--accent)]"
          style={{ color: 'var(--text-muted)' }}
          aria-label={isExpanded ? t('canvas.timeline.collapse') : t('canvas.timeline.expand')}
        >
          {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />}
          {isExpanded ? t('canvas.timeline.collapse') : t('canvas.timeline.expand')}
        </button>
      </div>
      {isExpanded && (
        <div className="flex flex-1 items-end gap-px overflow-x-auto px-3 pb-2">
          {timelineBuckets.map((bucket) => (
            <DockBar
              key={bucket.id}
              bucket={bucket}
              maxCount={maxCount}
              isSelected={selectedBuckets.includes(bucket.id)}
              onClick={handleBucketClick}
            />
          ))}
        </div>
      )}
    </div>
  )
}
